import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Toolbar,
  AppBar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { useUser } from '../hooks';
import { DrawerNavigation, MmcIcon, DRAWER_WIDTH, COLLAPSED_WIDTH } from '../components';
import { getTaskBgColor } from '../constants/taskConfig';
import {
  PageName,
  ApiResponse,
  MakeStatistic,
  MakeWithModelsStatistic,
  MakeWithSeriesStatistic,
  BodyStatistic,
  TypeStatistic,
  ColorStatistic,
  FlagStatistic,
  YearStatistic,
} from '../types';
import api from '../services/api';

// Chart colors
const CHART_COLORS = [
  '#1976d2', '#2196f3', '#03a9f4', '#00bcd4', '#009688',
  '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107',
  '#ff9800', '#ff5722', '#f44336', '#e91e63', '#9c27b0',
  '#673ab7', '#3f51b5', '#607d8b', '#795548', '#9e9e9e',
];

const PIE_COLORS = ['#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4'];

interface MMCStatisticsProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

// Custom tooltip for bar charts
const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1.5 }}>
        <Typography variant="body2" fontWeight="bold">{label}</Typography>
        <Typography variant="body2" color="primary">
          Count: {payload[0].value.toLocaleString()}
        </Typography>
      </Paper>
    );
  }
  return null;
};

// Custom tooltip for color chart
const ColorBarTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              bgcolor: data.hex || '#ccc',
              border: 1, borderColor: 'divider',
            }}
          />
          <Typography variant="body2" fontWeight="bold">{label}</Typography>
        </Box>
        <Typography variant="body2" color="primary">
          Count: {payload[0].value.toLocaleString()}
        </Typography>
      </Paper>
    );
  }
  return null;
};

// Format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

function MMCStatistics({ onLogout, onNavigate }: MMCStatisticsProps) {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const { user, loading: userLoading } = useUser();

  // Statistics data state
  const [makes, setMakes] = useState<MakeStatistic[]>([]);
  const [models, setModels] = useState<MakeWithModelsStatistic[]>([]);
  const [series, setSeries] = useState<MakeWithSeriesStatistic[]>([]);
  const [bodies, setBodies] = useState<BodyStatistic[]>([]);
  const [types, setTypes] = useState<TypeStatistic[]>([]);
  const [colors, setColors] = useState<ColorStatistic[]>([]);
  const [flags, setFlags] = useState<FlagStatistic[]>([]);
  const [years, setYears] = useState<YearStatistic[]>([]);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search filters for models and series tables
  const [modelSearch, setModelSearch] = useState('');
  const [seriesSearch, setSeriesSearch] = useState('');

  // Scale toggles for charts
  const [yearsScale, setYearsScale] = useState<'linear' | 'log'>('linear');
  const [makesScale, setMakesScale] = useState<'linear' | 'log'>('linear');
  const [bodiesScale, setBodiesScale] = useState<'linear' | 'log'>('linear');
  const [colorsScale, setColorsScale] = useState<'linear' | 'log'>('linear');
  const [flagsScale, setFlagsScale] = useState<'linear' | 'log'>('linear');

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Ref to prevent double fetching in StrictMode
  const hasFetched = useRef(false);

  // Fetch all statistics data
  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        makesRes,
        modelsRes,
        seriesRes,
        bodiesRes,
        typesRes,
        colorsRes,
        flagsRes,
        yearsRes,
      ] = await Promise.all([
        api.get<ApiResponse<MakeStatistic[]>>('statistics/mmc/makes'),
        api.get<ApiResponse<MakeWithModelsStatistic[]>>('statistics/mmc/models'),
        api.get<ApiResponse<MakeWithSeriesStatistic[]>>('statistics/mmc/series'),
        api.get<ApiResponse<BodyStatistic[]>>('statistics/mmc/bodies'),
        api.get<ApiResponse<TypeStatistic[]>>('statistics/mmc/types'),
        api.get<ApiResponse<ColorStatistic[]>>('statistics/mmc/colors'),
        api.get<ApiResponse<FlagStatistic[]>>('statistics/mmc/flags'),
        api.get<ApiResponse<YearStatistic[]>>('statistics/mmc/years'),
      ]);

      setMakes(makesRes.data.results);
      setModels(modelsRes.data.results);
      setSeries(seriesRes.data.results);
      setBodies(bodiesRes.data.results);
      setTypes(typesRes.data.results);
      setColors(colorsRes.data.results);
      setFlags(flagsRes.data.results);
      setYears(yearsRes.data.results);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
      setError('Failed to load statistics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchStatistics();
  }, []);

  // Sort and prepare data for charts
  const sortedMakes = useMemo(() => {
    return [...makes].sort((a, b) => b.count - a.count);
  }, [makes]);

  const sortedBodies = useMemo(() => {
    return [...bodies].sort((a, b) => b.count - a.count);
  }, [bodies]);

  const sortedTypes = useMemo(() => {
    return [...types].sort((a, b) => b.count - a.count);
  }, [types]);

  const sortedColors = useMemo(() => {
    return [...colors].sort((a, b) => b.count - a.count);
  }, [colors]);

  const sortedFlags = useMemo(() => {
    return [...flags].sort((a, b) => b.count - a.count);
  }, [flags]);

  const sortedYears = useMemo(() => {
    return [...years].sort((a, b) => a.year - b.year);
  }, [years]);

  // Filtered data for log scale (exclude zero counts to avoid log(0) = NaN)
  const chartMakes = useMemo(() => {
    return makesScale === 'log' ? sortedMakes.filter((m) => m.count > 0) : sortedMakes;
  }, [sortedMakes, makesScale]);

  const chartBodies = useMemo(() => {
    return bodiesScale === 'log' ? sortedBodies.filter((b) => b.count > 0) : sortedBodies;
  }, [sortedBodies, bodiesScale]);

  const chartColors = useMemo(() => {
    return colorsScale === 'log' ? sortedColors.filter((c) => c.count > 0) : sortedColors;
  }, [sortedColors, colorsScale]);

  const chartFlags = useMemo(() => {
    return flagsScale === 'log' ? sortedFlags.filter((f) => f.count > 0) : sortedFlags;
  }, [sortedFlags, flagsScale]);

  const chartYears = useMemo(() => {
    return yearsScale === 'log' ? sortedYears.filter((y) => y.count > 0) : sortedYears;
  }, [sortedYears, yearsScale]);

  // Flatten and sort all models by count
  const rankedModels = useMemo(() => {
    const allModels: { make: string; model: string; count: number }[] = [];
    models.forEach((make) => {
      make.models.forEach((model) => {
        allModels.push({
          make: make.label,
          model: model.label,
          count: model.count,
        });
      });
    });
    return allModels.sort((a, b) => b.count - a.count);
  }, [models]);

  // Flatten and sort all series by count
  const rankedSeries = useMemo(() => {
    const allSeries: { make: string; series: string; count: number }[] = [];
    series.forEach((make) => {
      make.series.forEach((s) => {
        allSeries.push({
          make: make.label,
          series: s.label,
          count: s.count,
        });
      });
    });
    return allSeries.sort((a, b) => b.count - a.count);
  }, [series]);

  // Filter models by search
  const filteredModels = useMemo(() => {
    if (!modelSearch) return rankedModels;
    const search = modelSearch.toLowerCase();
    return rankedModels.filter(
      (m) => m.make.toLowerCase().includes(search) || m.model.toLowerCase().includes(search)
    );
  }, [rankedModels, modelSearch]);

  // Filter series by search
  const filteredSeries = useMemo(() => {
    if (!seriesSearch) return rankedSeries;
    const search = seriesSearch.toLowerCase();
    return rankedSeries.filter(
      (s) => s.make.toLowerCase().includes(search) || s.series.toLowerCase().includes(search)
    );
  }, [rankedSeries, seriesSearch]);

  // Calculate totals
  const totalVehicles = useMemo(() => {
    return makes.reduce((sum, m) => sum + m.count, 0);
  }, [makes]);

  const totalModels = useMemo(() => {
    return models.reduce((sum, m) => sum + m.models.length, 0);
  }, [models]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  if (userLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Side Navigation Drawer */}
      <DrawerNavigation
        currentPage="statsMmc"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'width 0.2s, margin-left 0.2s',
          bgcolor: '#1976d2',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <BarChartIcon />
            <Typography variant="h6" noWrap component="div">
              MMC Statistics
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchStatistics}
              disabled={loading}
            >
              Refresh
            </Button>
            {user && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, ml: 2 }}>
                <Typography variant="body2">@{user.username}</Typography>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'width 0.2s, margin-left 0.2s',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" disableGutters>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('mmc', theme.palette.mode),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MmcIcon size={40} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h1">
                  Make, Model & Color Statistics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dataset overview and distribution analysis
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {totalVehicles.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Vehicles
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {makes.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Makes
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {totalModels.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Models
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Vehicle Types - Pie Chart */}
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Vehicle Types
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sortedTypes as any[]}
                          dataKey="count"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {sortedTypes.map((_, index) => (
                            <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => (typeof value === 'number' ? value.toLocaleString() : value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Years Distribution - Area Chart */}
            <Grid size={{ xs: 12, md: 6, lg: 8 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      Year Distribution
                    </Typography>
                    <ToggleButtonGroup
                      value={yearsScale}
                      exclusive
                      onChange={(_, value) => value && setYearsScale(value)}
                      size="small"
                    >
                      <ToggleButton value="linear">Linear</ToggleButton>
                      <ToggleButton value="log">Log</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartYears}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis
                          tickFormatter={formatNumber}
                          scale={yearsScale}
                          domain={yearsScale === 'log' ? [1, 'auto'] : [0, 'auto']}
                        />
                        <Tooltip formatter={(value) => (typeof value === 'number' ? value.toLocaleString() : value)} />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#1976d2"
                          fill="#e3f2fd"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Makes - Horizontal Bar Chart (All Makes) */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      Vehicle Makes ({chartMakes.length} total)
                    </Typography>
                    <ToggleButtonGroup
                      value={makesScale}
                      exclusive
                      onChange={(_, value) => value && setMakesScale(value)}
                      size="small"
                    >
                      <ToggleButton value="linear">Linear</ToggleButton>
                      <ToggleButton value="log">Log</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ height: Math.max(400, chartMakes.length * 22), maxHeight: 800, overflow: 'auto' }}>
                    <ResponsiveContainer width="100%" height={Math.max(400, chartMakes.length * 22)}>
                      <BarChart
                        data={chartMakes}
                        layout="vertical"
                        margin={{ left: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          tickFormatter={formatNumber}
                          scale={makesScale}
                          domain={makesScale === 'log' ? [1, 'auto'] : [0, 'auto']}
                        />
                        <YAxis type="category" dataKey="label" width={75} tick={{ fontSize: 11 }} interval={0} />
                        <Tooltip content={<CustomBarTooltip />} />
                        <Bar dataKey="count" fill="#1976d2" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Bodies - Horizontal Bar Chart */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      Body Styles ({chartBodies.length} total)
                    </Typography>
                    <ToggleButtonGroup
                      value={bodiesScale}
                      exclusive
                      onChange={(_, value) => value && setBodiesScale(value)}
                      size="small"
                    >
                      <ToggleButton value="linear">Linear</ToggleButton>
                      <ToggleButton value="log">Log</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ height: Math.max(400, chartBodies.length * 22), maxHeight: 800, overflow: 'auto' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartBodies}
                        layout="vertical"
                        margin={{ left: 100 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          tickFormatter={formatNumber}
                          scale={bodiesScale}
                          domain={bodiesScale === 'log' ? [1, 'auto'] : [0, 'auto']}
                        />
                        <YAxis type="category" dataKey="label" width={95} tick={{ fontSize: 12 }} interval={0} />
                        <Tooltip content={<CustomBarTooltip />} />
                        <Bar dataKey="count" fill="#4caf50" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Colors - Bar Chart with actual colors */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      Colors ({chartColors.length} total)
                    </Typography>
                    <ToggleButtonGroup
                      value={colorsScale}
                      exclusive
                      onChange={(_, value) => value && setColorsScale(value)}
                      size="small"
                    >
                      <ToggleButton value="linear">Linear</ToggleButton>
                      <ToggleButton value="log">Log</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ height: Math.max(400, chartColors.length * 22), maxHeight: 800, overflow: 'auto' }}>
                    <ResponsiveContainer width="100%" height={Math.max(400, chartColors.length * 22)}>
                      <BarChart
                        data={chartColors}
                        layout="vertical"
                        margin={{ left: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          tickFormatter={formatNumber}
                          scale={colorsScale}
                          domain={colorsScale === 'log' ? [1, 'auto'] : [0, 'auto']}
                        />
                        <YAxis type="category" dataKey="label" width={75} tick={{ fontSize: 12 }} interval={0} />
                        <Tooltip content={<ColorBarTooltip />} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {chartColors.map((color, index) => (
                            <Cell key={index} fill={color.hex || CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Flags - Bar Chart */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      Flags ({chartFlags.length} total)
                    </Typography>
                    <ToggleButtonGroup
                      value={flagsScale}
                      exclusive
                      onChange={(_, value) => value && setFlagsScale(value)}
                      size="small"
                    >
                      <ToggleButton value="linear">Linear</ToggleButton>
                      <ToggleButton value="log">Log</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ height: Math.max(400, chartFlags.length * 22), maxHeight: 800, overflow: 'auto' }}>
                    <ResponsiveContainer width="100%" height={Math.max(400, chartFlags.length * 22)}>
                      <BarChart
                        data={chartFlags}
                        layout="vertical"
                        margin={{ left: 120 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          tickFormatter={formatNumber}
                          scale={flagsScale}
                          domain={flagsScale === 'log' ? [1, 'auto'] : [0, 'auto']}
                        />
                        <YAxis type="category" dataKey="label" width={115} tick={{ fontSize: 11 }} interval={0} />
                        <Tooltip content={<CustomBarTooltip />} />
                        <Bar dataKey="count" fill="#ff9800" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Models - Ranked Table */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Models Ranked ({rankedModels.length} total)
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Search makes or models..."
                      value={modelSearch}
                      onChange={(e) => setModelSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: 250 }}
                    />
                  </Box>
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Make</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredModels.map((item, index) => (
                          <TableRow key={`${item.make}-${item.model}`} hover>
                            <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                            <TableCell>{item.make}</TableCell>
                            <TableCell>{item.model}</TableCell>
                            <TableCell align="right">{item.count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Series - Ranked Table */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Series Ranked ({rankedSeries.length} total)
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Search makes or series..."
                      value={seriesSearch}
                      onChange={(e) => setSeriesSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: 250 }}
                    />
                  </Box>
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Make</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Series</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSeries.map((item, index) => (
                          <TableRow key={`${item.make}-${item.series}`} hover>
                            <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                            <TableCell>{item.make}</TableCell>
                            <TableCell>{item.series}</TableCell>
                            <TableCell align="right">{item.count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Summary Table */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Summary
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Top Item</TableCell>
                          <TableCell align="right">Top Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Makes</TableCell>
                          <TableCell align="right">{makes.length}</TableCell>
                          <TableCell align="right">{sortedMakes[0]?.label || '-'}</TableCell>
                          <TableCell align="right">{sortedMakes[0]?.count.toLocaleString() || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Models</TableCell>
                          <TableCell align="right">{totalModels.toLocaleString()}</TableCell>
                          <TableCell align="right">{filteredModels[0] ? `${filteredModels[0].make} ${filteredModels[0].model}` : '-'}</TableCell>
                          <TableCell align="right">{filteredModels[0]?.count.toLocaleString() || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Body Styles</TableCell>
                          <TableCell align="right">{bodies.length}</TableCell>
                          <TableCell align="right">{sortedBodies[0]?.label || '-'}</TableCell>
                          <TableCell align="right">{sortedBodies[0]?.count.toLocaleString() || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Types</TableCell>
                          <TableCell align="right">{types.length}</TableCell>
                          <TableCell align="right">{sortedTypes[0]?.label || '-'}</TableCell>
                          <TableCell align="right">{sortedTypes[0]?.count.toLocaleString() || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Colors</TableCell>
                          <TableCell align="right">{colors.length}</TableCell>
                          <TableCell align="right">{sortedColors[0]?.label || '-'}</TableCell>
                          <TableCell align="right">{sortedColors[0]?.count.toLocaleString() || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Flags</TableCell>
                          <TableCell align="right">{flags.length}</TableCell>
                          <TableCell align="right">{sortedFlags[0]?.label || '-'}</TableCell>
                          <TableCell align="right">{sortedFlags[0]?.count.toLocaleString() || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Years</TableCell>
                          <TableCell align="right">{years.length}</TableCell>
                          <TableCell align="right">
                            {sortedYears.length > 0
                              ? [...sortedYears].sort((a, b) => b.count - a.count)[0]?.year
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {sortedYears.length > 0
                              ? [...sortedYears].sort((a, b) => b.count - a.count)[0]?.count.toLocaleString()
                              : '-'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default MMCStatistics;
